"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface WhyChooseUsReason {
  title: string;
  body: string;
  icon: LucideIcon;
  accent: string;
}

export interface WhyChooseUsSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  reasons: WhyChooseUsReason[];
  accent: string;
  gridCols?: 2 | 3;
}

export function WhyChooseUsSection({
  id = "why-choose-us",
  eyebrow,
  title,
  reasons,
  accent,
  gridCols = 3,
}: WhyChooseUsSectionProps) {
  return (
    <Section id={id} className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className={`mt-12 grid gap-5 ${gridCols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {reasons.map((r) => {
            const Icon = r.icon;
            return (
              <Reveal key={r.title} delay={60}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${r.accent} 12%, transparent)`,
                  }}
                >
                  <span
                    className="grid place-items-center h-11 w-11 rounded-xl"
                    style={{
                      background: `color-mix(in oklch, ${r.accent} 18%, transparent)`,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${r.accent} 35%, transparent)`,
                    }}
                  >
                    <Icon className="h-5 w-5" style={{ color: r.accent }} />
                  </span>
                  <h3 className="mt-4 text-lg">{r.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{r.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
